import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { HubSearchResult } from '../../types/hubSearch';
import { highlightMatch } from '../../utils/search';
import './HubQuickSearch.css';

export interface HubQuickSearchProps {
  open: boolean;
  onClose: () => void;
  getResults: (query: string) => HubSearchResult[];
  onSelect: (result: HubSearchResult) => void;
  placeholder?: string;
  ariaLabel?: string;
  hint?: React.ReactNode;
  renderIcon?: (result: HubSearchResult) => React.ReactNode;
  kindLabel?: (result: HubSearchResult) => string;
}

function defaultKindLabel(result: HubSearchResult): string {
  switch (result.kind) {
    case 'lead': return 'Lead';
    case 'contact': return 'Contact';
    case 'deal': return 'Deal';
    case 'project': return 'Project';
    case 'task': return 'Task';
    case 'comment': return 'Comment';
    case 'group': return 'Group';
    case 'opsTask': return 'Ops';
    case 'opsComment': return 'Comment';
    case 'proposal': return 'Proposal';
    case 'vendor': return 'Vendor';
    case 'inboxThread': return 'Inbox';
    case 'message': return 'Message';
    case 'wonDeal': return 'Deal';
    case 'designDeal': return 'Design';
    default: return result.section;
  }
}

const HubQuickSearch: React.FC<HubQuickSearchProps> = ({
  open,
  onClose,
  getResults,
  onSelect,
  placeholder = 'Search…',
  ariaLabel = 'Quick search',
  hint = 'Press Space anywhere on this page to open quick search',
  renderIcon,
  kindLabel = defaultKindLabel,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const results = useMemo(() => getResults(query), [getResults, query]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIndex(0);
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, results]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const handleSelect = useCallback(
    (result: HubSearchResult) => {
      onSelect(result);
      onClose();
    },
    [onSelect, onClose],
  );

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(i => Math.min(i + 1, Math.max(0, results.length - 1)));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(i => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter' && results[activeIndex]) {
        e.preventDefault();
        handleSelect(results[activeIndex]);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose, results, activeIndex, handleSelect]);

  useEffect(() => {
    if (!listRef.current) return;
    const active = listRef.current.querySelector('.hqs-item.active');
    active?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (!open) return null;

  let lastSection = '';

  return (
    <div className="hqs-overlay" role="presentation" onClick={onClose}>
      <div
        className="hqs-panel"
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        onClick={e => e.stopPropagation()}
      >
        <div className="hqs-input-row">
          <Search size={18} className="hqs-input-icon" />
          <input
            ref={inputRef}
            type="search"
            className="hqs-input"
            placeholder={placeholder}
            value={query}
            onChange={e => setQuery(e.target.value)}
            aria-label={ariaLabel}
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="hqs-kbd">esc</kbd>
        </div>
        {hint && <div className="hqs-hint">{hint}</div>}
        <div className="hqs-results pm-scroll" ref={listRef}>
          {results.length === 0 ? (
            <div className="hqs-empty">No matches</div>
          ) : (
            results.map((result, index) => {
              const showSection = result.section !== lastSection;
              lastSection = result.section;
              const titleHtml = query.trim()
                ? highlightMatch(result.title, query.trim())
                : result.title;
              return (
                <React.Fragment key={`${result.kind}-${result.id}-${index}`}>
                  {showSection && (
                    <div className="hqs-section-label">{result.section}</div>
                  )}
                  <button
                    type="button"
                    className={`hqs-item ${index === activeIndex ? 'active' : ''}`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => handleSelect(result)}
                  >
                    <span className="hqs-item-icon">
                      {renderIcon ? renderIcon(result) : <Search size={16} />}
                    </span>
                    <span className="hqs-item-text">
                      <span
                        className="hqs-item-title"
                        dangerouslySetInnerHTML={{ __html: titleHtml }}
                      />
                      {result.subtitle && (
                        <span className="hqs-item-sub">{result.subtitle}</span>
                      )}
                    </span>
                    <span className="hqs-item-kind">{kindLabel(result)}</span>
                  </button>
                </React.Fragment>
              );
            })
          )}
        </div>
        <div className="hqs-footer">
          <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
          <span><kbd>Enter</kbd> open</span>
          <span><kbd>Esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
};

export default HubQuickSearch;
